import { Controller } from 'stimulus';
import gsap from 'gsap';
import csrfToken from '../helpers/csrfToken';
import renderVacancyAgendaItem from '../helpers/renderVacancyAgendaItem';

const SCROLL_SHADOW_BREAKPOINT = 1200;
const SCROLL_SHADOW_WIDTH = 48;

class Agenda extends Controller {
  connect() {
    this.scrollContainer = this.element.parentNode;
  }

  boundingPosition() {
    const bounds = this.elementPosition(this.scrollContainer);

    if (window.innerWidth < SCROLL_SHADOW_BREAKPOINT) {
      bounds.width -= SCROLL_SHADOW_WIDTH;
    }

    return bounds;
  }

  // eslint-disable-next-line class-methods-use-this
  elementPosition(element) {
    return element.getBoundingClientRect();
  }

  // eslint-disable-next-line class-methods-use-this
  elementPositionBeforeTransform(element) {
    const parentRect = element.offsetParent.getBoundingClientRect();

    return {
      left: parentRect.left + element.offsetLeft,
      top: parentRect.top + element.offsetTop,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  triggerForElement(element) {
    return element.querySelector('.js-agenda-item-trigger');
  }

  // eslint-disable-next-line class-methods-use-this
  contentForElement(element) {
    return element.querySelector('.js-agenda-item-content');
  }

  onCloseClick() {
    this.resetItemsPositions();
  }

  onItemClick({ currentTarget }) {
    const item = currentTarget.parentNode;
    const trigger = this.triggerForElement(item);
    const content = this.contentForElement(item);

    const boundingPosition = this.boundingPosition();
    const elementPosition = this.elementPositionBeforeTransform(item);

    item.style.zIndex = '5';

    // Store original size so we can put it back on close...
    item.setAttribute('data-original-width', item.offsetWidth);
    item.setAttribute('data-original-height', item.style.height);

    // Show content and close button...
    content.style.display = 'block';

    // Fade/zoom out all other items in the agenda...
    this.hideAllItemsExcept(item);

    const startTime = new Date();
    const animationDuration = 0.75;

    gsap.to(item, {
      x: boundingPosition.left - elementPosition.left,
      y: boundingPosition.top - elementPosition.top,
      scale: 1,
      width: boundingPosition.width,
      height: boundingPosition.height,
      duration: animationDuration,
    });

    gsap.to(trigger, {
      autoAlpha: 0,
      duration: animationDuration,
    });

    this.loadVacancyAgendaItem(trigger.getAttribute('data-vacancy-agenda-item-id'))
      .then((res) => {
        // Wait for the animations above to finish before outputting the content...
        const endTime = new Date();
        const timeDifference = endTime.getTime() - startTime.getTime();
        const timeout = (animationDuration * 1000) - timeDifference;

        setTimeout(() => {
          this.outputVacancyAgendaItem(res, item);
        }, timeout > 0 ? timeout : 10);
      });
  }

  resetItemsPositions() {
    // Move items back to their original position...
    gsap.to(this.itemTargets, {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      onComplete: () => {
        setTimeout(() => {
          const contentElement = this.element.querySelector('.sh-agenda-modal__content.has-content');

          if (!contentElement) {
            return;
          }

          contentElement.classList.remove('has-content');

          contentElement.innerHTML = '';
        }, 10);
      },
    });

    this.resetZoomedItemsPositions();
  }

  resetZoomedItemsPositions() {
    // Find all items that are zoomed (should be one, but just to be sure)...
    const zoomedItems = this.itemTargets.filter((element) => (
      element.getAttribute('data-original-width') && element.getAttribute('data-original-height')
    ));

    zoomedItems.forEach((element) => {
      const trigger = this.triggerForElement(element);
      const content = this.contentForElement(element);

      trigger.style.visibility = 'visible';
      content.style.display = 'none';

      gsap.to(trigger, { opacity: 1 });

      gsap.to(element, {
        width: element.getAttribute('data-original-width'),
        height: element.getAttribute('data-original-height'),
        onComplete: () => {
          element.style.zIndex = 0;
        },
      });

      element.removeAttribute('data-original-width');
      element.removeAttribute('data-original-height');
    });
  }

  hideAllItemsExcept(element) {
    const items = this.itemTargets.filter((target) => target !== element);

    gsap.to(items, {
      scale: 0.95,
      opacity: 0,
    });
  }

  // eslint-disable-next-line class-methods-use-this
  outputVacancyAgendaItem(res, item) {
    const contentSection = item.querySelector('.sh-agenda-modal__content');

    if (!contentSection) {
      return;
    }

    contentSection.innerHTML = renderVacancyAgendaItem(res);

    contentSection.classList.add('has-content');

    gsap.to(contentSection, {
      autoAlpha: 1,
    });
  }

  // eslint-disable-next-line class-methods-use-this
  loadVacancyAgendaItem(vacancyAgendaItemId) {
    return fetch(this.data.get('api'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken(),
      },
      body: JSON.stringify({
        id: vacancyAgendaItemId,
      }),
    })
      .then((res) => res.json());
  }
}

Agenda.targets = [
  'item',
];

export default Agenda;
